import { Row, Col, Card, Spinner, Alert } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { useAuth } from '../../hooks/useAuth'
import { useClientFilter } from '../../hooks/useClientFilter'
import api from '../../services/api'
import { tieneModulo } from '../../utils/modulos'
import './Dashboard.css'
import { formatCurrency } from '../../utils/formatters'

const Dashboard = () => {
  const { user } = useAuth()
  const { selectedClientId, selectedClient, isFiltering, clientLabel } = useClientFilter()

  // Obtener estadísticas de clientes
  const { data: clientsStats, isLoading: loadingClients } = useQuery(
    ['clients-stats', user?.id, selectedClientId],
    async () => {
      if (isFiltering && selectedClient) {
        const estado = selectedClient.estado
        return {
          total: 1,
          activos: estado === 'activo' ? 1 : 0,
          inactivos: estado === 'inactivo' || estado === 'suspendido' || estado === 'baja' ? 1 : 0
        }
      }
      const response = await api.get('/clients/clients/')
      const clients = response.data.results || response.data || []
      return {
        total: clients.length,
        activos: clients.filter(c => c.estado === 'activo').length,
        inactivos: clients.filter(c => c.estado === 'inactivo' || c.estado === 'suspendido' || c.estado === 'baja').length
      }
    },
    { enabled: !!user, staleTime: 0 }
  )

  // Obtener estadísticas financieras
  const { data: financialStats, isLoading: loadingFinancial } = useQuery(
    ['financial-stats', user?.id, selectedClientId],
    async () => {
      try {
        const [invoices, payments] = await Promise.all([
          api.get('/financial/invoices/'),
          api.get('/financial/payments/')
        ])
        
        const invoicesData = invoices.data.results || invoices.data || []
        const paymentsData = payments.data.results || payments.data || []
        
        const totalFacturado = invoicesData.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0)
        const totalPagado = paymentsData
          .filter(p => p.estado === 'aplicado')
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        
        return {
          totalFacturado,
          totalPagado,
          pendiente: totalFacturado - totalPagado,
          totalFacturas: invoicesData.length,
          totalPagos: paymentsData.length
        }
      } catch (error) {
        return {
          totalFacturado: 0,
          totalPagado: 0,
          pendiente: 0,
          totalFacturas: 0,
          totalPagos: 0
        }
      }
    },
    { enabled: !!user, staleTime: 0 }
  )

  // Obtener estadísticas contables
  const { data: accountingStats, isLoading: loadingAccounting } = useQuery(
    ['accounting-stats', user?.id, selectedClientId],
    async () => {
      try {
        const [accounts, transactions] = await Promise.all([
          api.get('/accounting/accounts/'),
          api.get('/accounting/transactions/')
        ])
        
        const accountsData = accounts.data.results || accounts.data || []
        const transactionsData = transactions.data.results || transactions.data || []
        
        return {
          totalCuentas: accountsData.filter(a => a.is_active).length,
          totalTransacciones: transactionsData.length,
          transaccionesPendientes: transactionsData.filter(t => !t.is_posted).length
        }
      } catch (error) {
        return {
          totalCuentas: 0,
          totalTransacciones: 0,
          transaccionesPendientes: 0
        }
      }
    },
    // Sin el módulo, la petición devuelve 403 y deja errores en la consola del contador: la
    // tarjeta no existe para él, así que tampoco el dato.
    { enabled: !!user && tieneModulo(user, 'contabilidad'), staleTime: 0 }
  )

  const isLoading = loadingClients || loadingFinancial || loadingAccounting

  return (
    <div className="dashboard-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Dashboard</h1>
        <div className="text-muted">
          Bienvenido, <strong>{user?.username || 'Usuario'}</strong>
        </div>
      </div>

      {isFiltering && (
        <Alert variant="info" className="mb-4 py-2">
          Mostrando datos del cliente: <strong>{clientLabel(selectedClient) || `#${selectedClientId}`}</strong>
        </Alert>
      )}

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {/* Estadísticas de Clientes */}
          <Row className="mb-4">
            <Col md={12}>
              <h3 className="mb-3">Clientes{isFiltering ? ' (filtrado)' : ''}</h3>
            </Col>
            <Col md={4}>
              <Card className="stat-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Total Clientes</Card.Title>
                      <h2 className="mb-0">{clientsStats?.total || 0}</h2>
                    </div>
                    <div className="stat-icon">👥</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stat-card stat-card-success">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Activos</Card.Title>
                      <h2 className="mb-0">{clientsStats?.activos || 0}</h2>
                    </div>
                    <div className="stat-icon">✅</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stat-card stat-card-warning">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Inactivos</Card.Title>
                      <h2 className="mb-0">{clientsStats?.inactivos || 0}</h2>
                    </div>
                    <div className="stat-icon">⏸️</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Estadísticas Financieras */}
          <Row className="mb-4">
            <Col md={12}>
              <h3 className="mb-3">Financiero</h3>
            </Col>
            <Col md={3}>
              <Card className="stat-card stat-card-primary">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Total Facturado</Card.Title>
                      <h4 className="mb-0">
                        {formatCurrency((financialStats?.totalFacturado || 0))}
                      </h4>
                    </div>
                    <div className="stat-icon">💰</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card stat-card-success">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Total Pagado</Card.Title>
                      <h4 className="mb-0">
                        {formatCurrency((financialStats?.totalPagado || 0))}
                      </h4>
                    </div>
                    <div className="stat-icon">✅</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card stat-card-warning">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Pendiente</Card.Title>
                      <h4 className="mb-0">
                        {formatCurrency((financialStats?.pendiente || 0))}
                      </h4>
                    </div>
                    <div className="stat-icon">⏳</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Facturas</Card.Title>
                      <h2 className="mb-0">{financialStats?.totalFacturas || 0}</h2>
                    </div>
                    <div className="stat-icon">📄</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Estadísticas Contables */}
          {tieneModulo(user, 'contabilidad') && (
          <Row>
            <Col md={12}>
              <h3 className="mb-3">Contabilidad</h3>
            </Col>
            <Col md={4}>
              <Card className="stat-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Cuentas Activas</Card.Title>
                      <h2 className="mb-0">{accountingStats?.totalCuentas || 0}</h2>
                    </div>
                    <div className="stat-icon">📊</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stat-card stat-card-primary">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Total Transacciones</Card.Title>
                      <h2 className="mb-0">{accountingStats?.totalTransacciones || 0}</h2>
                    </div>
                    <div className="stat-icon">📝</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stat-card stat-card-warning">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="text-muted small mb-1">Pendientes</Card.Title>
                      <h2 className="mb-0">{accountingStats?.transaccionesPendientes || 0}</h2>
                    </div>
                    <div className="stat-icon">⏳</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          )}
        </>
      )}
    </div>
  )
}

export default Dashboard
